// 2048: slide every tile one way; tiles with the same number that meet join into one. A new tile (2, or 4 one time
// in ten) lands on an empty square after each move. The win is a 2048 tile; play can go on for a higher one.
// Pure functions over a plain state; the interface is twenty48/ui.ts. Our own implementation of the public-domain
// rules (the original by Gabriele Cirulli, 2014, is MIT-licensed; nothing of it is copied).
import { seeded } from "../lib/rng"

export interface T48State {
  size: number
  cells: number[] // row-major; 0 is empty, else the tile's number
  score: number
  moves: number
  /** Tiles added so far. With the seed it decides the next tile, so a saved game and a daily replay the same. */
  spawns: number
  seed: string
  /** A 2048 tile has been made (the win). It stays true while play goes on. */
  reached: boolean
  attempt?: number
}
export type Dir = "left" | "right" | "up" | "down"
export const DIRS: readonly Dir[] = ["left", "right", "up", "down"]
export const SIZES = [4, 5, 6] as const
export type Size = (typeof SIZES)[number]
export const GOAL = 2048

/** The next tile for a board: where (among the empty squares) and what, from the seed and the spawn count. */
function spawn(s: T48State): T48State {
  const empties = s.cells.flatMap((v, i) => (v === 0 ? [i] : []))
  if (!empties.length) return s
  const rand = seeded(`${s.seed}:${s.spawns}`)
  const at = empties[Math.floor(rand() * empties.length)]!
  const cells = s.cells.slice()
  cells[at] = rand() < 0.1 ? 4 : 2
  return { ...s, cells, spawns: s.spawns + 1 }
}

export function deal(seed: string, size: number = 4): T48State {
  const n = SIZES.includes(size as Size) ? size : 4
  let s: T48State = { size: n, cells: Array<number>(n * n).fill(0), score: 0, moves: 0, spawns: 0, seed, reached: false }
  s = spawn(spawn(s))
  return s
}

/** One row or column slid toward index 0: tiles close up, equal neighbours join once, left to right. */
export function slideLine(line: readonly number[]): { line: number[]; gained: number } {
  const packed = line.filter((v) => v !== 0)
  const out: number[] = []
  let gained = 0
  for (let i = 0; i < packed.length; i++) {
    if (i + 1 < packed.length && packed[i] === packed[i + 1]) {
      out.push(packed[i]! * 2)
      gained += packed[i]! * 2
      i++
    } else out.push(packed[i]!)
  }
  while (out.length < line.length) out.push(0)
  return { line: out, gained }
}

/** The square indexes of each line in the order the tiles slide for `dir` (the first index is where they go). */
function lines(size: number, dir: Dir): number[][] {
  const out: number[][] = []
  for (let a = 0; a < size; a++) {
    const idx: number[] = []
    for (let b = 0; b < size; b++) {
      const r = dir === "up" ? b : dir === "down" ? size - 1 - b : a
      const c = dir === "left" ? b : dir === "right" ? size - 1 - b : a
      idx.push(r * size + c)
    }
    out.push(idx)
  }
  return out
}

/** The board after a slide and its new tile, or the same state when nothing can move that way. */
export function move(s: T48State, dir: Dir): T48State {
  const cells = s.cells.slice()
  let gained = 0
  let changed = false
  for (const idx of lines(s.size, dir)) {
    const before = idx.map((i) => cells[i]!)
    const { line, gained: g } = slideLine(before)
    gained += g
    line.forEach((v, k) => { if (cells[idx[k]!] !== v) { changed = true; cells[idx[k]!] = v } })
  }
  if (!changed) return s
  const next: T48State = { ...s, cells, score: s.score + gained, moves: s.moves + 1 }
  next.reached = s.reached || cells.some((v) => v >= GOAL)
  return spawn(next)
}

export function canMove(s: T48State): boolean {
  return DIRS.some((d) => move(s, d) !== s)
}
export const isOver = (s: T48State): boolean => !canMove(s)
export const maxTile = (s: T48State): number => Math.max(0, ...s.cells)

/** How good a board looks: empty squares, pairs ready to join, and the biggest tile in a corner. */
function value(s: T48State): number {
  const n = s.size
  let empties = 0, pairs = 0
  for (let i = 0; i < s.cells.length; i++) {
    const v = s.cells[i]!
    if (v === 0) { empties++; continue }
    const r = Math.floor(i / n), c = i % n
    if (c + 1 < n && s.cells[i + 1] === v) pairs++
    if (r + 1 < n && s.cells[i + n] === v) pairs++
  }
  const big = maxTile(s)
  const corners = [0, n - 1, n * (n - 1), n * n - 1]
  const corner = corners.some((i) => s.cells[i] === big) ? Math.log2(big || 1) : 0
  return empties * 10 + pairs * 4 + corner * 3 + Math.log2(big || 1)
}

/** A good direction to slide next: the move that leaves the best board two moves ahead, or null when stuck. */
export function hint(s: T48State): Dir | null {
  let best: Dir | null = null
  let bestScore = -Infinity
  for (const d of DIRS) {
    const a = move(s, d)
    if (a === s) continue
    let inner = -Infinity
    for (const e of DIRS) {
      const b = move(a, e)
      if (b === a) continue
      inner = Math.max(inner, value(b))
    }
    const score = inner === -Infinity ? value(a) - 100 : inner
    if (score > bestScore) { bestScore = score; best = d }
  }
  return best
}
